/**
 * Duty Time Service
 * API calls for duty time tracking and compliance
 */

import { api } from './api';

// ── Types ────────────────────────────────────────────────────────────────────

export interface WindowStatus {
  used: number;
  limit: number;
  remaining: number;
  percent: number;
}

export interface DutyAlert {
  type: 'warning' | 'critical' | 'exceeded';
  limitType: string;
  message: string;
  percentUsed: number;
}

export interface CurrentFDPStatus {
  active: boolean;
  dutyPeriodId: number | null;
  startTime: string | null;
  elapsedHours: number;
  limit: number;
  remaining: number;
}

export interface LastRestStatus {
  durationHours: number | null;
  location: string | null;
  wasAtHomeBase: boolean;
  meetsMinimum: boolean;
  minimumRequired: number;
}

export interface PilotDutyStatus {
  pilotId: number;
  pilotName: string;
  homeBase: string | null;
  windows: {
    hours7day: WindowStatus;
    hours30day: WindowStatus;
    hours90day: WindowStatus;
    hours365day: WindowStatus;
  };
  currentFDP: CurrentFDPStatus;
  lastRest: LastRestStatus;
  alerts: DutyAlert[];
  overallStatus: 'green' | 'amber' | 'red';
}

export interface ValidationResult {
  allowed: boolean;
  warnings: string[];
  violations: string[];
}

export interface DutyPeriod {
  id: number;
  pilotId: number;
  dutyStartTime: string;
  dutyEndTime: string | null;
  startLocation: string | null;
  endLocation: string | null;
  isAtHomeBase: boolean;
  totalFlightTime: number | null;
  dutyDurationHours: number | null;
  status: 'ACTIVE' | 'COMPLETED' | 'CANCELLED';
  tripId: number | null;
  notes: string | null;
}

export interface DutyAuditEntry {
  id: number;
  pilotId: number;
  eventType: string;
  limitType: string | null;
  currentValue: number | null;
  limitValue: number | null;
  percentUsed: number | null;
  message: string;
  createdAt: string;
  triggeredBy: { firstName: string; lastName: string } | null;
}

// ── API calls ────────────────────────────────────────────────────────────────

interface StatusResponse {
  success: boolean;
  data: PilotDutyStatus;
}

interface AllStatusResponse {
  success: boolean;
  data: PilotDutyStatus[];
  count: number;
}

interface AlertsResponse {
  success: boolean;
  data: Array<DutyAlert & { pilotId: number; pilotName: string }>;
  count: number;
}

interface HistoryResponse {
  success: boolean;
  data: {
    dutyPeriods: DutyPeriod[];
    auditLog: DutyAuditEntry[];
  };
}

interface ValidationResponse {
  success: boolean;
  data: ValidationResult;
}

interface DutyPeriodResponse {
  success: boolean;
  data: DutyPeriod;
  warnings?: string[];
}

/**
 * Get duty status for the current user (pilot) or all pilots (admin/scheduler).
 */
export async function getDutyStatus(): Promise<PilotDutyStatus | PilotDutyStatus[]> {
  const res = await api.get<StatusResponse | AllStatusResponse>('/duty-time/status');
  return res.data;
}

/**
 * Get duty status for all pilots (admin/scheduler).
 */
export async function getAllPilotsStatus(): Promise<PilotDutyStatus[]> {
  const res = await api.get<AllStatusResponse>('/duty-time/status');
  return res.data;
}

/**
 * Get duty status for a specific pilot.
 */
export async function getPilotDutyStatus(pilotId: number): Promise<PilotDutyStatus> {
  const res = await api.get<StatusResponse>(`/duty-time/status/${pilotId}`);
  return res.data;
}

/**
 * Start a new duty period.
 */
export async function startDutyPeriod(data: {
  pilotId?: number;
  dutyStartTime: string;
  startLocation?: string;
  tripId?: number;
  notes?: string;
}): Promise<DutyPeriod> {
  const res = await api.post<DutyPeriodResponse>('/duty-time/duty-period', data);
  return res.data;
}

/**
 * End a duty period.
 */
export async function endDutyPeriod(id: number, data: {
  dutyEndTime: string;
  endLocation?: string;
  totalFlightTime?: number;
  notes?: string;
}): Promise<DutyPeriod> {
  const res = await api.patch<DutyPeriodResponse>(`/duty-time/duty-period/${id}`, data);
  return res.data;
}

/**
 * Pre-validate a crew assignment against duty time limits.
 */
export async function validateAssignment(
  pilotId: number,
  estimatedHours: number,
): Promise<ValidationResult> {
  const res = await api.get<ValidationResponse>(
    `/duty-time/validate-assignment?pilotId=${pilotId}&estimatedHours=${estimatedHours}`
  );
  return res.data;
}

/**
 * Get all active duty time alerts across all pilots.
 */
export async function getDutyAlerts() {
  const res = await api.get<AlertsResponse>('/duty-time/alerts');
  return res.data;
}

/**
 * Get duty period history and audit trail for a pilot.
 */
export async function getDutyHistory(pilotId: number) {
  const res = await api.get<HistoryResponse>(`/duty-time/history/${pilotId}`);
  return res.data;
}
